import React from "react";
import { Box, Checkbox, Grid, Typography } from "@mui/material";
import {
  FEATURE_CATEGORIES_WITHOUT_FEATURES,
  getFeatureCategoryDisplayName,
} from "../../utils/featureCategories";

const defaultCardSx = {
  p: 2,
  borderRadius: "8px",
  backgroundColor: "#FFFFFF",
  minHeight: "150px",
  display: "flex",
  flexDirection: "column",
};

const defaultHeaderSx = {
  display: "flex",
  alignItems: "center",
  mb: 1,
  padding: "8px",
  backgroundColor: "#B7BFCC",
  borderRadius: "8px",
};

const defaultTitleSx = {
  color: "#111214",
  fontWeight: 600,
  fontSize: "20px",
  fontFamily: "Roboto",
};

const defaultBodySx = {
  pl: 2,
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: "12px",
};

const defaultRegularFeatureSx = (isSelected) => ({
  fontFamily: "Roboto",
  fontSize: "16px",
  color: isSelected ? "#111214" : "#666",
  fontWeight: isSelected ? 500 : 400,
});

const defaultAddOnSectionSx = {
  mt: 1,
};

const defaultAddOnHeaderSx = {
  backgroundColor: "#E8EAEE",
  p: 1,
  borderRadius: "8px",
  boxShadow: "-2px 4px 5px 0px #00000040",
};

const defaultAddOnTitleSx = {
  fontSize: "16px",
  fontFamily: "Roboto",
  fontWeight: 500,
  color: "#111214",
};

const defaultAddOnRowSx = {
  display: "flex",
  alignItems: "center",
  mt: 1,
};

const defaultAddOnFeatureSx = (isSelected) => ({
  color: isSelected ? "#111214" : "#666",
  fontWeight: isSelected ? 500 : 400,
});

export default function FeatureCategoryGrid({
  categories,
  getCategoryFeatures,
  mandatoryCategories = [],
  getCategoryState,
  onCategoryToggle,
  isFeatureSelected,
  onAddOnToggle,
  isAddOnDisabled,
  isAddOnFeature = (feature) => feature.isDefault === false,
  resolveFeatureLabel = (feature) => feature.name,
  resolveAddOnLabel = (feature) => feature.displayName || feature.name,
  alwaysVisibleCategories = FEATURE_CATEGORIES_WITHOUT_FEATURES,
  footer = null,
  gridSpacing = 3,
  itemProps = { xs: 12, sm: 6, md: 4 },
  cardSx = {},
  headerSx = {},
  headerPaddingByCategory,
  titleSx = {},
  bodySx = {},
  regularFeatureSx,
  addOnSectionSx = {},
  addOnHeaderSx = {},
  addOnTitleSx = {},
  addOnRowSx = {},
  addOnFeatureSx,
  emptyTextSx = { fontStyle: "italic", color: "#666" },
}) {
  const mandatoryCategorySet = new Set(mandatoryCategories);

  return (
    <Grid container spacing={gridSpacing}>
      {categories.map((category) => {
        const categoryFeatures = getCategoryFeatures(category);
        const regularFeatures = categoryFeatures.filter(
          (feature) => !isAddOnFeature(feature),
        );
        const addOnFeatures = categoryFeatures.filter(isAddOnFeature);

        if (
          categoryFeatures.length === 0 &&
          !alwaysVisibleCategories.includes(category)
        ) {
          return null;
        }

        const isMandatory = mandatoryCategorySet.has(category);
        const categoryState =
          getCategoryState?.(category, regularFeatures, addOnFeatures) || {};
        const categoryPadding = headerPaddingByCategory
          ? headerPaddingByCategory(category, isMandatory)
          : undefined;

        return (
          <Grid item {...itemProps} key={category}>
            <Box sx={{ ...defaultCardSx, ...cardSx }}>
              <Box
                sx={{
                  ...defaultHeaderSx,
                  ...(categoryPadding ? { padding: categoryPadding } : {}),
                  ...headerSx,
                }}
              >
                {!isMandatory && onCategoryToggle && (
                  <Checkbox
                    checked={categoryState.checked || false}
                    indeterminate={categoryState.indeterminate || false}
                    onChange={() =>
                      onCategoryToggle(category, regularFeatures, addOnFeatures)
                    }
                    disabled={categoryState.disabled || false}
                    size={categoryState.checkboxSize}
                    sx={{ "&.Mui-checked": { color: "#20A8E0" } }}
                  />
                )}
                <Typography variant="subtitle1" sx={{ ...defaultTitleSx, ...titleSx }}>
                  {getFeatureCategoryDisplayName(category)}
                </Typography>
              </Box>

              <Box sx={{ ...defaultBodySx, ...bodySx }}>
                {regularFeatures.map((feature) => {
                  const selected =
                    isMandatory || isFeatureSelected?.(category, feature, false);

                  return (
                    <Typography
                      key={feature.id}
                      variant="body2"
                      sx={
                        regularFeatureSx
                          ? regularFeatureSx(selected, feature, category)
                          : defaultRegularFeatureSx(selected)
                      }
                    >
                      {resolveFeatureLabel(feature, category)}
                    </Typography>
                  );
                })}

                {categoryFeatures.length === 0 && (
                  <Typography variant="body2" sx={emptyTextSx}>
                    No features available
                  </Typography>
                )}

                {addOnFeatures.length > 0 && (
                  <Box sx={{ ...defaultAddOnSectionSx, ...addOnSectionSx }}>
                    <Box sx={{ ...defaultAddOnHeaderSx, ...addOnHeaderSx }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ ...defaultAddOnTitleSx, ...addOnTitleSx }}
                      >
                        Add-Ons
                      </Typography>
                    </Box>

                    {addOnFeatures.map((feature) => {
                      const selected = isFeatureSelected?.(category, feature, true);

                      return (
                        <Box key={feature.id} sx={{ ...defaultAddOnRowSx, ...addOnRowSx }}>
                          {onAddOnToggle && (
                            <Checkbox
                              checked={selected || false}
                              onChange={() => onAddOnToggle(category, feature)}
                              disabled={isAddOnDisabled?.(category, feature) || false}
                              size={categoryState.checkboxSize}
                              sx={{ "&.Mui-checked": { color: "#20A8E0" } }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            sx={
                              addOnFeatureSx
                                ? addOnFeatureSx(selected, feature, category)
                                : defaultAddOnFeatureSx(selected)
                            }
                          >
                            {resolveAddOnLabel(feature, category)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        );
      })}

      {footer && (
        <Grid item xs={12} sx={{ mt: 3 }}>
          {footer}
        </Grid>
      )}
    </Grid>
  );
}
